/**
 * Decide whether prior, explicit consent is required before loading
 * non-essential telemetry (Firebase Performance, Sentry).
 *
 * We detect region from the browser's IANA time zone. This is intentional:
 * it needs no network request and no geo-IP lookup (which would itself be a
 * third-party call), so the check that decides whether we may track cannot
 * itself leak data. It is over-inclusive on purpose — every `Europe/*` zone
 * is treated as consent-required, which sweeps in a few non-EU countries
 * (e.g. Switzerland, Norway) that have comparable rules anyway. The cost of a
 * false positive is only that a handful of extra users see the banner.
 *
 * Trade-off: a VPN or a travelling user can be mis-classified. If you need
 * legal-grade accuracy, replace `isConsentRequiredRegion` with a server-side
 * geo header (Cloud Function / hosting rewrite) and keep the same interface.
 */

const CONSENT_REQUIRED_ZONE_PREFIXES = ['Europe/'];

// A few EEA/consent-relevant zones that are not under the Europe/ prefix.
const CONSENT_REQUIRED_ZONES = new Set([
  'Atlantic/Canary', // Spain
  'Atlantic/Madeira', // Portugal
  'Atlantic/Azores', // Portugal
  'Atlantic/Reykjavik', // Iceland (EEA)
]);

export function resolveTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

export function isConsentRequiredRegion(
  timeZone: string | null = resolveTimeZone(),
): boolean {
  if (!timeZone) {
    // Unknown region → be conservative and require consent.
    return true;
  }

  if (CONSENT_REQUIRED_ZONES.has(timeZone)) {
    return true;
  }

  return CONSENT_REQUIRED_ZONE_PREFIXES.some((prefix) =>
    timeZone.startsWith(prefix),
  );
}
